<template>
  <button
    :type="type"
    :disabled="disabled"
    class="rounded-sm p-4 text-sm leading-none font-medium"
    :class="{
      'active:scale-[0.99]': !disabled,
      'button-primary': variant === 'primary' && !ignoreBranding,
      'button-primary-no-branding': variant === 'primary' && ignoreBranding,
      'button-secondary': variant === 'secondary' && !ignoreBranding,
      'button-secondary-no-branding': variant === 'secondary' && ignoreBranding,
      'button-error': variant === 'error',
      'button-muted': variant === 'muted',
    }"
  >
    <slot></slot>
  </button>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { getColorFromCssVar } from "@/util/common";
import type { InputButtonVariant } from "@/types/CommonTypes";

const {
  type = "button",
  disabled = false,
  variant = "primary",
  ignoreBranding = false,
} = defineProps<{
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
  variant?: InputButtonVariant;
  ignoreBranding?: boolean;
}>();

const buttonColor = computed(() => {
  if (disabled) {
    return "#F1F4F7";
  }

  return getColorFromCssVar("--branding-button");
});

const textColor = computed(() => {
  if (disabled) {
    return "#C2C6CD";
  }
  return getColorFromCssVar("--branding-button-text");
});

const shadowColor = computed(() => {
  if (!buttonColor.value) {
    return "";
  }
  return `${buttonColor.value}3F`;
});

const buttonSecondaryColor = computed(() => {
  if (disabled) {
    return "#F1F4F7";
  }
  return `${buttonColor.value}20`;
});

const textSecondaryColor = computed(() => {
  if (disabled) {
    return "#C2C6CD";
  }
  return buttonColor.value;
});

const buttonErrorColor = computed(() => {
  if (disabled) {
    return "#F1F4F7";
  }
  return "#FF595E1A";
});

const textErrorColor = computed(() => {
  if (disabled) {
    return "#C2C6CD";
  }
  return "#E51D23";
});

const buttonMutedColor = computed(() => {
  if (disabled) {
    return "#C2C6CD";
  }
  return "#FFFFFF";
});

const textMutedColor = computed(() => {
  if (disabled) {
    return "#C2C6CD";
  }
  return "#868D9C";
});

const borderMutedColor = computed(() => {
  if (disabled) {
    return "#C2C6CD";
  }
  return "#EBEEF5";
});
</script>

<style scoped>
@reference "@/tailwind.css";

.button-primary-no-branding {
  background-color: v-bind(getColorFromCssVar("--color-primary"));
  color: #ffffff;
}

.button-secondary-no-branding {
  background-color: rgba(55, 105, 214, 0.1);
  color: v-bind(getColorFromCssVar("--color-primary"));
}

.button-primary {
  background-color: v-bind(buttonColor);
  color: v-bind(textColor);
  box-shadow: 0 4px 4px v-bind(shadowColor);
}

.button-secondary {
  background-color: v-bind(buttonSecondaryColor);
  color: v-bind(textSecondaryColor);
}

.button-error {
  background-color: v-bind(buttonErrorColor);
  color: v-bind(textErrorColor);
}

.button-muted {
  background-color: v-bind(buttonMutedColor);
  color: v-bind(textMutedColor);
  border: 2px solid v-bind(borderMutedColor);
}
</style>
